<template>
    <div class="main-block request-management">
        <Breadcrumbs :isCheck="true" @navigate="handleNavigation" />
        <div class="div col-12 p-0 d-flex align-items-start">
            <h4>{{ $t('title.update') }}</h4>
        </div>
        <section class="col-12 mx-auto align-self-md-center scrollable-block">
            <div class="half-design row">
                <div class="col-12 form-block">
                    <div class="col-12 col-md-6 col-lg-5 mx-auto pt-5">
                        <b-form @submit.prevent="onSubmit" enctype="multipart/form-data">
                            <!-- Service Type Select -->
                            <div class="mb-3 text-left">
                                <label class="mb-1" :class="serviceState" for="typeSelect">
                                    {{ $t('title.Please_select_your_service') }}
                                </label>
                                <v-select id="type-select" v-model="formFields.typeSelect" @input="onServiceSelect"
                                    :class="serviceState" :options="serviceOptions" size="sm"
                                    :placeholder="formFields.services" />
                                <b-form-invalid-feedback :state="!formErrors.has('services_id')">
                                    {{ formErrors.first('services_id') }}
                                </b-form-invalid-feedback>
                            </div>

                            <!-- Organization Name Input -->
                            <div class="mb-3 text-left">
                                <label for="name" class="mb-1">
                                    {{ $t('title.Please_enter_your_organization_name') }}
                                </label>
                                <b-form-input id="name" type="text" v-model="formFields.name" />
                                <b-form-invalid-feedback :state="!formErrors.has('name')">
                                    {{ formErrors.first('name') }}
                                </b-form-invalid-feedback>
                            </div>

                            <!-- Summary Description Field -->
                            <div class="mb-3 text-left">
                                <label for="summary_description" class="mb-1" :class="summaryDescriptionState">
                                    {{ $t('title.Summary_Description') }}
                                </label>
                                <b-form-textarea id="summary_description" v-model="formFields.summary_description"
                                    :placeholder="$t('title.Please_enter_summary_description')" rows="3"
                                    :class="summaryDescriptionState" @input="validateSummaryDescription">
                                </b-form-textarea>

                                <b-form-invalid-feedback :state="!formErrors.has('summary_description')">
                                    {{ formErrors.first('summary_description') }}
                                </b-form-invalid-feedback>
                            </div>

                            <div v-for="(label, key) in dynamicFields" :key="key" class="mb-3 text-left">
                                <label :for="label" class="mb-1">
                                    {{ $t(`title.${label}`) }}
                                </label>
                                <b-input-group
                                    v-if="label !== 'google_service_account_credentials_json' && label !== 'product_service'">
                                    <b-form-input :id="String(label)" v-model="formFields[label]"
                                        :type="passwordVisibility[label] ? 'text' : 'password'"
                                        :placeholder="$t(`title.${label}`)"></b-form-input>
                                    <b-input-group-append>
                                        <b-button @click="toggleVisibility(label)" variant="light">
                                            <b-icon :icon="passwordVisibility[label] ? 'eye-slash' : 'eye'"></b-icon>
                                        </b-button>
                                    </b-input-group-append>
                                </b-input-group>
                                <v-select v-else-if="label === 'product_service'" :id="String(label)"
                                    v-model="formFields[label]"
                                    :placeholder="$t(`title.${label}`, { defaultValue: label })"
                                    :options="productsServicesOptions" label="label" />
                                <b-form-file v-else :id="String(label)"
                                    v-model="formFields.google_service_account_credentials_json"
                                    :placeholder="$t(`title.${label}`)"></b-form-file>
                                <b-form-invalid-feedback :state="!formErrors.has(label)">
                                    {{ formErrors.first(label) }}
                                </b-form-invalid-feedback>
                            </div>

                            <div class="mb-3 text-left">
                                <label for="status" class="mb-1" :class="statusState">
                                    {{ $t('title.SelectStatus') }}
                                </label>
                                <v-select id="status" v-model="formFields.status" :class="statusState"
                                    :options="statusOptions" size="sm" required />
                                <b-form-invalid-feedback :state="!formErrors.has('status')">
                                    {{ formErrors.first('status') }}
                                </b-form-invalid-feedback>
                            </div>

                            <div class="mb-3 text-left">
                                <label for="limit" class="mb-1">
                                    {{ $t('title.SelectLimit') }}
                                </label>
                                <b-form-datepicker id="date_picker" v-model="formFields.selected_date" reset-button
                                    today-button locale="en" class="w-100">
                                </b-form-datepicker>

                                <b-form-invalid-feedback :state="!formErrors.has('limit')">
                                    {{ formErrors.first('limit') }}
                                </b-form-invalid-feedback>
                            </div>


                            <!-- Submit Button -->
                            <b-button type="submit" variant="primary" class="mb-3 w-100" :disabled="loader">
                                <div class="spinner-border spinner-border-sm text-light" role="status" v-if="loader">
                                </div>
                                {{ $t('title.Submit') }}
                            </b-button>
                            <div class="d-flex gap-2">
                                <b-button type="button" variant="secondary" class="mb-3 col" @click.prevent="resetForm">
                                    {{ $t('title.clear') }}
                                </b-button>
                                <b-button type="button" variant="danger" class="mb-3 col" @click.prevent="onCancel">
                                    {{ $t('title.cancel') }}
                                </b-button>
                            </div>
                            <b-form-invalid-feedback v-if="formErrors.general">
                                {{ formErrors.general }}
                            </b-form-invalid-feedback>
                        </b-form>
                    </div>
                </div>
            </div>
            <InputChangeConfirmationModal ref="inputChangeConfirmationModal" @back="goBackToDestination"/>
        </section>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { reactive } from 'vue';
import { request } from "@/Util/Request";
import Error from "@/Util/Error";
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import InputChangeConfirmationModal from "../modal/InputChangeConfirmationModal";

export default {
    data() {
        return {
            formFields: reactive({
                services_id: null,
                name: '',
                summary_description: '',
                typeSelect: null,
                services: 'Select a service',
                clientId: '',
                clientSecret: '',
                tenantId: '',
                microsoft_client_id: '',
                microsoft_client_secret: '',
                microsoft_tenant_id: '',
                grant_type: '',
                username: '',
                password: '',
                instance_url: '',
                DomainName: '',
                DomainAdminEmail: '',
                code: '',
                redirect_uri: '',
                google_service_account_credentials_json: null,
                status: '',
                selected_date: null,
                product_service: null,
                notion_database_id: '',
                notion_secret: '',
            }),
            existing_google_credentials_url: '',
            formErrors: new Error({}),
            serviceState: '',
            serviceOptions: [],
            statusOptions: [
                { label: this.$t('title.Active'), value: '1' },
                { label: this.$t('title.Inactive'), value: '0' },
            ],
            organizationId: null,
            dynamicFields: [],
            allProductServicesOptions: {
                salesforce: [
                    { label: this.$t('title.CRM'), value: 'CRM' },
                    { label: this.$t('title.Analytics'), value: 'Analytics' },
                    { label: this.$t('title.Net_Zero_Cloud'), value: 'Net Zero Cloud' },
                    { label: this.$t('title.Financial_Services_Cloud'), value: 'Financial Services Cloud' },
                    { label: this.$t('title.Communications_Cloud'), value: 'Communications Cloud' },
                    { label: this.$t('title.Education_Cloud'), value: 'Education Cloud' },
                ],
                zoho: [
                    { label: this.$t('title.Zoho_CRM'), value: 'CRM' },
                ],
            },

            passwordVisibility: {},
            originalForm: {},
            isFormDirty: false,
            nextRoute: ''
        };
    },
    components: { Breadcrumbs, InputChangeConfirmationModal },
    computed: {
        ...mapGetters(['loader']),
        productsServicesOptions() {
            if (!this.formFields.typeSelect) {
                return [];
            }
            const selectedServiceName = this.formFields.typeSelect.label.toLowerCase();
            if (selectedServiceName.includes('salesforce')) {
                return this.allProductServicesOptions.salesforce;
            } else if (selectedServiceName.includes('zoho')) {
                return this.allProductServicesOptions.zoho;
            }
            return [];
        },
        statusState() {
            return this.formFields.status === null
                ? ''
                : this.formErrors.status
                    ? 'is-invalid'
                    : 'is-valid';
        },
        summaryDescriptionState() {
            return this.formFields.summary_description === '' ? '' : this.formErrors.$errors.summary_description ? 'is-invalid' : 'is-valid';
        },
    },

    methods: {
        goBackToDestination() {
            if(this.nextRoute) {
                this.$router.push(this.nextRoute);
            }
        },
        handleNavigation(crumb) {
            console.log(this.isFormDirty, crumb);
            if (this.isFormDirty) {
                this.$refs['inputChangeConfirmationModal'].handleToggleModal();
                this.nextRoute = crumb.path; // Store next route
            } else {
                this.$router.push(crumb.path); // Navigate if no unsaved changes
            }
        },
        markAsDirty() {
            this.isFormDirty = true;
        },
        checkForChanges() {
            this.isFormDirty = JSON.stringify(this.formFields) !== JSON.stringify(this.originalForm);
        },
        resetForm() {
            this.formFields = reactive({
                name: '',
                summary_description: '',
                selected_date: null,
                typeSelect: { value: '', label: this.$t('title.Please_select_your_service') },
                status: { value: '1', label: this.$t('title.Active') },
            });
        },
        initializeVisibility() {
            Object.keys(this.formFields).forEach((key) => {
                if (!(key in this.passwordVisibility)) {
                    this.$set(this.passwordVisibility, key, false);
                }
            });
        },
        toggleVisibility(field) {
            this.passwordVisibility[field] = !this.passwordVisibility[field];
        },
        async fetchServices() {
            try {
                const response = await request({ method: 'get', url: '/admin/services' });
                const data = response.data;
                this.serviceOptions = data.map(service => ({
                    value: service.id,
                    label: service.name,
                    fields: service.fields || {},
                }));
            } catch (error) {
                console.error('Error fetching services:', error);
            }
        },

        onServiceSelect(selected) {
            if (selected) {
                this.formFields.typeSelect = selected;
                this.formFields.services_id = selected.value;
                this.getfields(selected);
            } else {
                this.formFields.services_id = null;
                this.dynamicFields = [];
            }
        },

        getfields(selectedService) {
            if (selectedService && selectedService.fields) {
                this.dynamicFields = Object.values(selectedService.fields);
            } else {
                this.dynamicFields = [];
            }
        },

        validateSummaryDescription() {
            const summaryDescription = this.formFields.summary_description;
            if (typeof summaryDescription !== 'string') {
                this.formErrors.$errors.summary_description = [this.$t('title.summaryDescriptionRequired')];
                return;
            }
            if (!summaryDescription || summaryDescription.trim().length < 10) {
                this.formErrors.$errors.summary_description = [this.$t('title.summaryDescriptionMinLength')];
            } else {
                delete this.formErrors.$errors.summary_description;
            }
        },

        async fetchOrganizationData(id) {
            try {
                const response = await request({ method: 'get', url: `/admin/integrations/${id}` });
                const data = response;
                this.formFields.name = data.name || '';
                this.formFields.summary_description = data.summary_description || '';
                const selectedService = this.serviceOptions.find(option => option.value === data.services_id);
                if (selectedService) {
                    this.formFields.typeSelect = selectedService;
                    this.formFields.services_id = selectedService.value;
                    this.getfields(selectedService);
                }
                this.formFields.client_id = data.client_id || '';
                this.formFields.client_secret = data.client_secret || '';
                this.formFields.tenant_id = data.tenant_id || '';
                this.formFields.is_active = data.is_active || '';
                this.formFields.selected_date = data.limit || null;
                this.formFields.microsoft_client_id = data.microsoft_client_id || '';
                this.formFields.microsoft_client_secret = data.microsoft_client_secret || '';
                this.formFields.microsoft_tenant_id = data.microsoft_tenant_id || '';
                this.formFields.code = data.code || '';
                this.formFields.redirect_uri = data.redirect_uri || '';
                this.formFields.grant_type = data.grant_type || '';
                this.formFields.username = data.username || '';
                this.formFields.password = data.password || '';
                this.formFields.instance_url = data.instance_url || '';
                this.formFields.DomainAdminEmail = data.DomainAdminEmail || '';
                this.formFields.DomainName = data.DomainName || '';
                this.existing_google_credentials_url = data.google_service_account_credentials_json || '';
                this.formFields.status = data.is_active ? "Active" : "InActive";
                this.formFields.product_service =
                this.productsServicesOptions.find(option => option.value === data.product_service) || null;
                this.formFields.api_access_token = data.api_access_token || '';
                this.formFields.base_url = data.base_url || '';
                this.formFields.api_key = data.api_key || '';
                this.formFields.notion_database_id = data.notion_database_id || '';
                this.formFields.notion_secret = data.notion_secret || '';
                this.originalForm = JSON.parse(JSON.stringify(this.formFields));
            } catch (error) {
                console.error('Error fetching organization data:', error);
            }
        },

        async onSubmit() {
            let formData = new FormData();
            console.log(this.formFields);
            formData.append("name", this.formFields.name || "");
            formData.append("summary_description", this.formFields.summary_description || "");
            formData.append("services_id", this.formFields.services_id || (this.formFields.typeSelect ? this.formFields.typeSelect.value : ""));
            formData.append("is_active", (this.formFields.status === "InActive" || (this.formFields.status.label && this.formFields.status.label === "Inactive")) ? 0 : 1);
            formData.append("tenant_id", this.formFields.tenantId || "");
            formData.append("limit", this.formFields.selected_date || "");
            formData.append("microsoft_client_id", this.formFields.microsoft_client_id || "");
            formData.append("microsoft_client_secret", this.formFields.microsoft_client_secret || "");
            formData.append("microsoft_tenant_id", this.formFields.microsoft_tenant_id || "");
            formData.append("code", this.formFields.code || "");
            formData.append("redirect_uri", this.formFields.redirect_uri || "");
            formData.append("grant_type", this.formFields.grant_type || "");
            formData.append("client_id", this.formFields.client_id || "");
            formData.append("client_secret", this.formFields.client_secret || "");
            formData.append("username", this.formFields.username || "");
            formData.append("password", this.formFields.password || "");
            formData.append("product_service", this.formFields.product_service ? this.formFields.product_service.value : "");
            formData.append("instance_url", this.formFields.instance_url || "");
            formData.append("DomainName", this.formFields.DomainName || "");
            formData.append("DomainAdminEmail", this.formFields.DomainAdminEmail || "");
            formData.append("api_access_token", this.formFields.api_access_token || "");
            formData.append("base_url", this.formFields.base_url || "");
            formData.append("api_key", this.formFields.api_key || "");
            formData.append("notion_database_id", this.formFields.notion_database_id || "");
            formData.append("notion_secret", this.formFields.notion_secret || "");

            if (this.formFields.google_service_account_credentials_json) {
                formData.append("google_service_account_credentials_json", this.formFields.google_service_account_credentials_json);
            } else {
                formData.append("google_service_account_credentials_json", this.existing_google_credentials_url || "");
            }


            try {
                const response = await request({
                    method: 'post',
                    url: `/admin/integrations-update/${this.organizationId}`,
                    data: formData,
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                if (response.message && response.message.includes("successfully")) {
                    this.showToast();
                    setTimeout(() => { this.$router.push({ name: 'Integrations' }); }, 1000);
                } else {
                    throw new Error(response.data.message || 'Error updating data');
                }
            } catch (error) {
                this.formErrors = new Error({});
                if (error?.data?.errors) {
                    const errors = error.data.errors;
                    this.formErrors.$errors.clientId = errors.client_id || '';
                    this.formErrors.$errors.name = errors.name || '';
                    this.formErrors.$errors.typeSelect = errors.services_id || '';
                    this.formErrors.$errors.client_id = errors.client_id || '';
                    this.formErrors.$errors.client_secret = errors.client_secret || '';
                    this.formErrors.$errors.username = errors.username || '';
                    this.formErrors.$errors.client_secret = errors.client_secret || '';
                    this.formErrors.$errors.clientSecret = errors.client_secret || '';
                    this.formErrors.$errors.tenantId = errors.tenant_id || '';
                    this.formErrors.$errors.status = errors.is_active || '';
                    this.formErrors.$errors.api_access_token = errors.api_access_token || '';
                    this.formErrors.$errors.limit = errors.limit || '';
                    this.formErrors.$errors.general = "Please fix the errors and try again.";
                    return false;
                } else {
                    this.formErrors.general = "Something went wrong. Please try again.";
                }
            }
        },

        reloadPage() {
            this.$router.push({ name: 'Integrations' });
        },

        showToast() {
            const h = this.$createElement;
            const vNodesMsg = h(
                'p',
                { class: ['text-center', 'mb-0'] },
                [this.$t('title.dataUpdated')]
            );
            const vNodesTitle = h(
                'div',
                { class: ['d-flex', 'flex-grow-1', 'align-items-baseline', 'mr-2'] },
                [h('strong', { class: 'mr-2' }, this.$t('title.dataUpdatedTitle'))]
            );
            this.$bvToast.toast([vNodesMsg], {
                title: [vNodesTitle],
                solid: true,
                variant: 'success',
            });
        },

        onCancel() {
            this.$router.push({ name: 'Integrations' });
        },
    },
    watch: {
        formFields: {
            deep: true,
            handler(newVal, oldVal) {
                this.initializeVisibility(); // Ensure visibility state is updated when new fields are added
                this.checkForChanges();
            }
        }
    },
    created() {
        const id = this.$route.params.id;
        this.organizationId = id;
        this.fetchServices().then(() => {
            this.fetchOrganizationData(id);
        });
        this.initializeVisibility();
    },
};
</script>
